import { useMemo, useState, ReactNode, ReactElement, useEffect, createContext, useCallback } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { AuthGuardContextType, CombinedUserInfo, ProductFeature } from '@localstack/types';

import {
  UserService,
  useApiEffect,
  useRoutes,
  useGlobalSwr,
  useApiGetter,
  getSelectedInstance,
  useLocalstackStatus,
  useSnackbar,
  extractFeaturePartitions,
  hasFeatureBase,
  hasOnlySnowflakeAccess,
} from '@localstack/services';

import { AppRoute } from '~/config';

import {
  getUserInfo,
  storeUserInfo,
  updatePreSignInPageURL,
  clearAuthStorage,
  retrieveImpersonationMarker,
  storeImpersonationMarker,
} from '~/util/storage';

import { SF_ONLY_ACCESSIBLE_SERVICES } from '~/constants';
import { useAuthenticatedRouteGuard } from '~/views/customer/Account/common';

import { ImpersonationWrapper } from '../components/ImpersonationWrapper';

/**
 * Routes which can strictly be accessed only if a user is not authenticated.
 */
const GUEST_ONLY_ROUTES = [
  AppRoute.SIGN_IN,
  AppRoute.SIGN_UP,
  AppRoute.RECOVER,
  AppRoute.RECOVER_ACTIVATE,
  AppRoute.ACCOUNT_ACTIVATE,
  AppRoute.SSO_START,
];

/**
 * Routes which can be accessed by both authenticated / unauthenticated users.
 * SSO paths are in this list becaause users can still connect other accounts while being authenticated
 * For example: from /settings/authentication page.
 * We don't want unintended redirect happen when these URLs are opened in a popup while being logged in.
 */
const AUTH_OPTIONAL_ROUTES = [
  AppRoute.LAUNCHPAD,
  AppRoute.NOT_FOUND,
  AppRoute.UNSUBSCRIBE_ACTIVATE,
  // Invite-accept must work for both new (create account) and existing
  // authenticated users. Keeping it guest-only bounced logged-in invitees
  // (e.g. with an expired trial) away before they could join the workspace.
  AppRoute.ACCEPT_INVITATION,
  AppRoute.SSO_CALLBACK,
  AppRoute.SSO_LOADING,
  AppRoute.AUTH_RELAY,
];

const PUBLIC_ROUTES = [...AUTH_OPTIONAL_ROUTES, ...GUEST_ONLY_ROUTES];

interface AuthGuardProviderProps {
  children: ReactNode;
}

export const AuthGuardContext = createContext<AuthGuardContextType>({
  userInfo: null,
  reloadUserInfo: () => Promise.resolve(),
});

/**
 * This map allows to configure custom routes to redirect to for specific routes
 */
const PreSignInRoutingMap: Record<string, string> = {
  [AppRoute.REDIRECT]: `${AppRoute.SIGN_UP}?plan=free`,
  [AppRoute.MARKETPLACE_ACTIVATE]: `${AppRoute.SIGN_UP}?plan=marketplace`,
  [AppRoute.AZURE_MARKETPLACE_ACTIVATE]: `${AppRoute.SIGN_UP}?plan=marketplace`,
};

/** Method to match a route pattern to a redirect route, since some routes might have a changing part e.g. with a token */
const getSignInRoute = (activeRoutePath: string): string | undefined => {
  for (const [pattern, redirect] of Object.entries(PreSignInRoutingMap)) {
    if (matchPath(pattern, activeRoutePath)) {
      return redirect;
    }
  }
  return AppRoute.SIGN_IN;
};

/**
 * This component makes sure private routes are unavailable from direct navigation.
 * It also takes care of any auth-related stuff, like refreshing auth tokens.
 */
export const AuthGuardProvider = ({ children }: AuthGuardProviderProps): ReactElement => {
  const { goto } = useRoutes();
  const { pathname: activeRoutePath, search } = useLocation();
  const { clearAll: clearSwrCache } = useGlobalSwr();
  const navigate = useNavigate();

  const { showSnackbar } = useSnackbar();

  const localStorageUserInfo = getUserInfo();
  const [userInfo, setUserInfo] = useState<Optional<CombinedUserInfo>>(localStorageUserInfo);

  const instance = getSelectedInstance();
  const isAuthenticated = userInfo?.roles.includes('authenticated');
  const isImpersonationMode = !!retrieveImpersonationMarker();

  const routeMatchesPath = (route: string, path: string) => {
    const regex = new RegExp(route.replace(/:[^:/]+/g, '[^/]+'));
    return regex.test(path);
  };

  const isPublicRoute = useMemo(
    () => PUBLIC_ROUTES.some((route) => routeMatchesPath(route, activeRoutePath)),
    [activeRoutePath],
  );

  const isGuestOnlyRoute = useMemo(
    () => GUEST_ONLY_ROUTES.some((route) => routeMatchesPath(route, activeRoutePath)),
    [activeRoutePath],
  );

  const isHomeOrPublic = routeMatchesPath(AppRoute.HOME, activeRoutePath) || isPublicRoute;

  const clientOverrides = instance ? { endpoint: instance.endpoint } : {};
  const { running } = useLocalstackStatus(clientOverrides, !isHomeOrPublic);

  // extract the service name from the pathname (e.g. apigatewayv2)
  const extractServiceName = (path: string): string | undefined => {
    const match = path.match(/\/resources\/([^/]+)/); // browser compatible regex (< Safari 16.4)

    return match?.[1];
  };

  // Note `getUser` invalidation will cause a full-app re-render
  const { data: loadedUserInfo } = useApiGetter(
    UserService,
    'getUser',
    [],
    { enable: !!isAuthenticated }, // `!!` is important here!
  );

  const { getUser } = useApiEffect(UserService, ['getUser']);

  const { featureNames, featureVariants } = useMemo(
    () => extractFeaturePartitions(userInfo?.features ?? []),
    [userInfo],
  );

  useEffect(() => {
    // For some reason, userInfo state doesn't get updated if the state is changed in another window and it still picks up the old state
    // this was discovered while moving the sso redirection logic to a new window and even after the finalizeSso call, userInfo was undefined
    // meanwhile it has some actual value in the other window. Handling it with useEffect does the job
    setUserInfo(localStorageUserInfo);
  }, [localStorageUserInfo?.user.email]);

  useEffect(() => {
    if (!loadedUserInfo) return;

    setUserInfo(loadedUserInfo);
    storeUserInfo(loadedUserInfo);
  }, [loadedUserInfo]);

  useEffect(() => {
    if (!running) {
      return;
    }

    const serviceName = extractServiceName(activeRoutePath);

    if (!serviceName) {
      return;
    }

    const hasFeatureInLicense = hasFeatureBase(
      featureNames,
      featureVariants,
      ProductFeature.FEATURE_LOCALSTACK_AWS_PROVIDER,
      `/^${serviceName}:.+$/`,
    );

    const hasFeature = (feature: ProductFeature, variant?: string) =>
      hasFeatureBase(featureNames, featureVariants, feature, variant);

    const snowflakeFilter = !hasOnlySnowflakeAccess(hasFeature) || SF_ONLY_ACCESSIBLE_SERVICES.includes(serviceName);
    const hasAccessToService = hasFeatureInLicense && snowflakeFilter;

    if (!hasAccessToService) {
      const { referrer } = document;

      if (referrer) {
        navigate(-1);
      } else {
        goto(AppRoute.HOME);
      }

      // In the future we could expand this by making the regex more generic and blocking other pages like pods
      showSnackbar({
        message: 'This service is not available in your current subscription',
        severity: 'error',
      });
    }
  }, [activeRoutePath, running, userInfo]);

  const handleStopImpersonation = useCallback(async () => {
    const impersonatedUserId = retrieveImpersonationMarker();

    if (!impersonatedUserId) return;

    await clearSwrCache();

    // clear impersonation marker
    storeImpersonationMarker(null);

    await reloadUserInfo();

    // navigate back to the impersonated user's account
    goto(AppRoute.ADMIN_ACCOUNT, { id: impersonatedUserId });
  }, []);

  const reloadUserInfo = useCallback(async () => {
    const newUserInfo = await getUser();
    setUserInfo(newUserInfo);
    return newUserInfo;
  }, []);

  // if user navigates to a private route while being unauthenticated:
  // * clear local storage from any remaining tokens or cache
  // * remember current page visited before redirection
  // * redirect to sign-in page
  // User should be automatically redirected to the previously requested page
  // after successful login. This is also true for logins after signups.
  useEffect(() => {
    if (!isPublicRoute && !isAuthenticated) {
      clearAuthStorage();
      clearSwrCache();
      // remember current page visited before redirection
      const params = new URLSearchParams(search);
      const stringParams = params.toString();

      updatePreSignInPageURL(stringParams ? `${activeRoutePath}?${stringParams}` : activeRoutePath);

      // redirect to sigin page
      const routeToGoTo = getSignInRoute(activeRoutePath);
      goto(routeToGoTo);
    }
  }, [isPublicRoute, isAuthenticated]);

  useEffect(() => {
    // Hide recaptcha badge on login
    const badge = document.querySelector<HTMLElement>('.grecaptcha-badge');
    if (userInfo && badge) badge.style.visibility = 'hidden';
  }, [userInfo]);

  useAuthenticatedRouteGuard({ isPublicRoute, isGuestOnlyRoute, userInfo });

  const value = useMemo(
    () => ({
      userInfo,
      reloadUserInfo,
    }),
    [userInfo, reloadUserInfo],
  );

  // we are on a public route, or user is authenticated
  if (isPublicRoute || isAuthenticated) {
    return (
      <AuthGuardContext.Provider value={value}>
        {isImpersonationMode ? (
          <ImpersonationWrapper onStop={handleStopImpersonation}>{children}</ImpersonationWrapper>
        ) : (
          children
        )}
      </AuthGuardContext.Provider>
    );
  }

  // permission denied, redirection will be handled by `useEffect` section
  return <></>;
};
